<template>
  <div class="known-business-entity-card-wrapper relative inline" :title="title">
    <!-- Text -->
    <span v-bind="$attrs">
      <slot name="before" />
      <div class="inline" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
        <ModelEditLink
          v-if="
            knownBusinessEntityContact &&
            !slotHasCombobox /* Comboboxes handle their own edit functionality, it conflicts with the ModelEditLink */
          "
          :model="knownBusinessEntityContact"
          :model-conversion-function="(kbec: KnownBusinessEntityContact) => kbec.person"
          :creation-parameters="creationParameters"
          :double-click="doubleClick"
          force-manager-update
          no-title
        >
          <slot />
        </ModelEditLink>
        <slot v-else />

        <span
          v-if="
            showVerificationWarning &&
            !!knownBusinessEntityContact?.knownBusinessEntity &&
            !knownBusinessEntityContact?.knownBusinessEntity?.registeredBusinessEntity
          "
          :title="`Let op! ${knownBusinessEntityContact?.knownBusinessEntity?.name} is niet geverifieerd.`"
        >
          <FontAwesomeIcon
            :icon="['fas', 'triangle-exclamation']"
            class="text-warning dark:text-warning-dark"
          />
        </span>
      </div>
      <slot name="after" />
    </span>
    <!-- Card -->
    <div
      v-if="isOpen && knownBusinessEntityContact && !disabled && false"
      class="surface border-primary dark:border-primary-dark divide-gray-lighten-2 absolute z-60 w-80 space-y-2 divide-y rounded-md border p-2 shadow-md"
      @mouseenter="onCardMouseEnter"
      @mouseleave="onCardMouseLeave"
    >
      <div class="flex items-start justify-between">
        <div class="font-semibold">{{ knownBusinessEntityContact?.person.fullName }}</div>
        <GeneralButton
          :icon="['fas', 'xmark']"
          variant="transparent"
          color="surface"
          size="large"
          class="min-h-0! p-0!"
          @click.stop="isOpen = false"
        />
      </div>

      <!-- Business Information -->
      <div v-if="knownBusinessEntityContact?.knownBusinessEntity?.name">
        <div class="text-gray-lighten-1 font-medium">Bedrijf</div>
        <div class="flex items-center space-x-1">
          <FontAwesomeIcon :icon="['fas', 'building']" size="sm" title="Bedrijf" />
          <span>{{ knownBusinessEntityContact?.knownBusinessEntity?.name }}</span>
        </div>
      </div>

      <!-- Contact Information -->
      <div v-if="hasContactInfo">
        <div class="text-gray-lighten-1 font-medium">Contact</div>
        <div class="space-y-1">
          <a
            v-if="knownBusinessEntityContact?.email"
            :href="`mailto:${knownBusinessEntityContact?.email}`"
            class="flex items-center space-x-1"
          >
            <FontAwesomeIcon :icon="['fas', 'envelope']" size="sm" title="Email" />
            <span>{{ knownBusinessEntityContact?.email }}</span>
          </a>
          <a
            v-if="knownBusinessEntityContact?.phoneNumber"
            :href="`tel:${knownBusinessEntityContact?.phoneNumber}`"
            class="flex items-center space-x-1"
          >
            <FontAwesomeIcon :icon="['fas', 'phone']" size="sm" title="Telefoon" />
            <span>{{ knownBusinessEntityContact?.phoneNumber }}</span>
          </a>
        </div>
      </div>

      <!-- Address Information -->
      <div v-if="hasAddress">
        <div class="text-gray-lighten-1 font-medium">Adres</div>
        <div>
          {{ knownBusinessEntityContact?.person.address?.street }}
          {{ knownBusinessEntityContact?.person.address?.houseNumber }}<br />
          {{ knownBusinessEntityContact?.person.address?.postalCode }}
          {{ knownBusinessEntityContact?.person.address?.country }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type VNode, useSlots } from "vue"
import InputCombobox from "@/components/inputs/input_combobox/InputCombobox.vue"
import type { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import GeneralButton from "@/components/inputs/GeneralButton.vue"
import ModelEditLink from "@/components/misc/ModelEditLink.vue"

defineOptions({
  inheritAttrs: false
})

const props = withDefaults(
  defineProps<{
    knownBusinessEntityContact?: KnownBusinessEntityContact | null | undefined
    disabled?: boolean
    showVerificationWarning?: boolean
    /**
     * Additional parameters for creating a new KnownBusinessEntityContact.
     */
    creationParameters?: object
    doubleClick?: boolean
  }>(),
  {
    showVerificationWarning: true,
    doubleClick: true
  }
)

const title = computed(() => {
  const parts = []

  if (props.knownBusinessEntityContact?.knownBusinessEntity?.name) {
    parts.push(props.knownBusinessEntityContact.knownBusinessEntity.name)
  }

  if (props.knownBusinessEntityContact?.phoneNumber) {
    parts.push(props.knownBusinessEntityContact.phoneNumber)
  }

  if (props.knownBusinessEntityContact?.email) {
    parts.push(props.knownBusinessEntityContact.email)
  }

  return parts.join(" | ")
})

const slots = useSlots()

function vnodeContainsComponent(vnodes: VNode[], component: object): boolean {
  for (const vnode of vnodes) {
    if (vnode.type === component) {
      return true
    }

    if (
      Array.isArray(vnode.children) &&
      vnodeContainsComponent(vnode.children as VNode[], component)
    ) {
      return true
    }
  }

  return false
}

const slotHasCombobox = computed(() => {
  const vnodes = slots.default?.()
  return vnodes ? vnodeContainsComponent(vnodes, InputCombobox) : false
})

const isOpen = ref(false)
const showTimeout = ref<number | null>(null)
const closeTimeout = ref<number | null>(null)
const isHoveringCard = ref(false)
const isHoveringWrapper = ref(false)

const hasContactInfo = computed(() => {
  return props.knownBusinessEntityContact?.email || props.knownBusinessEntityContact?.phoneNumber
})

const hasAddress = computed(() => {
  return (
    props.knownBusinessEntityContact?.person.address?.street ||
    props.knownBusinessEntityContact?.person.address?.houseNumber ||
    props.knownBusinessEntityContact?.person.address?.postalCode ||
    props.knownBusinessEntityContact?.person.address?.country
  )
})

function onMouseEnter() {
  if (props.disabled) return
  isHoveringWrapper.value = true
  if (closeTimeout.value) {
    clearTimeout(closeTimeout.value)
    closeTimeout.value = null
  }
  showTimeout.value = window.setTimeout(() => {
    if (isHoveringWrapper.value) {
      isOpen.value = true
    }
    // Only show after X sec of active hovering
  }, 2500)
}

function onMouseLeave() {
  if (props.disabled) return
  isHoveringWrapper.value = false
  if (showTimeout.value) {
    clearTimeout(showTimeout.value)
    showTimeout.value = null
  }
  if (closeTimeout.value) {
    clearTimeout(closeTimeout.value)
  }
  closeTimeout.value = window.setTimeout(() => {
    if (!isHoveringCard.value && !isHoveringWrapper.value) {
      isOpen.value = false
    }
  }, 500)
}

function onCardMouseEnter() {
  isHoveringCard.value = true
  isOpen.value = true
  if (closeTimeout.value) {
    clearTimeout(closeTimeout.value)
    closeTimeout.value = null
  }
}

function onCardMouseLeave() {
  isHoveringCard.value = false
  if (closeTimeout.value) {
    clearTimeout(closeTimeout.value)
  }
  closeTimeout.value = window.setTimeout(() => {
    if (!isHoveringWrapper.value) {
      isOpen.value = false
    }
  }, 1000)
}
</script>
